import React, { useState, useEffect, useRef } from 'react';
import './NowPlaying.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function NowPlaying() {
  const [playbackState, setPlaybackState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const pollIntervalRef = useRef(null);

  const fetchPlaybackState = async () => {
    try {
      const response = await fetch(`${API_BASE}/playback/state`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch playback state');
      }

      const data = await response.json();
      setPlaybackState(data);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      console.error('Playback state error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  const handlePlayPause = async () => {
    try {
      const endpoint = playbackState?.isPlaying ? 'pause' : 'play';
      const response = await fetch(`${API_BASE}/playback/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Playback control failed');
      }

      // Immediately fetch updated state
      setTimeout(fetchPlaybackState, 300);
    } catch (err) {
      console.error('Play/pause error:', err);
      setError(err.message);
    }
  };

  const handleNext = async () => {
    try {
      const response = await fetch(`${API_BASE}/playback/next`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Next track failed');
      }

      setTimeout(fetchPlaybackState, 300);
    } catch (err) {
      console.error('Next error:', err);
      setError(err.message);
    }
  };

  const handlePrevious = async () => {
    try {
      const response = await fetch(`${API_BASE}/playback/previous`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Previous track failed');
      }

      setTimeout(fetchPlaybackState, 300);
    } catch (err) {
      console.error('Previous error:', err);
      setError(err.message);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchPlaybackState();

    // Poll every 5 seconds
    pollIntervalRef.current = setInterval(fetchPlaybackState, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return null; // Don't show anything while loading
  }

  if (error) {
    return null; // Hide on error
  }

  if (!playbackState || playbackState.noActiveDevice || !playbackState.track) {
    return null; // No active playback
  }

  const { track, isPlaying, progressMs } = playbackState;
  const progressPercent = track.duration ? (progressMs / track.duration) * 100 : 0;

  return (
    <div className={`now-playing ${isExpanded ? 'expanded' : ''}`}>
      <div className="now-playing-compact" onClick={() => setIsExpanded(!isExpanded)}>
        {track.albumArt && (
          <img src={track.albumArt} alt={track.album} className="now-playing-album-art" />
        )}
        <div className="now-playing-info">
          <div className="now-playing-track">{track.name}</div>
          <div className="now-playing-artist">{track.artists.join(', ')}</div>
        </div>
        <button
          className={`now-playing-play-btn ${isPlaying ? 'playing' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handlePlayPause();
          }}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
      </div>

      {isExpanded && (
        <div className="now-playing-expanded">
          <div className="now-playing-progress-bar">
            <div
              className="now-playing-progress-fill"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <div className="now-playing-controls">
            <button onClick={handlePrevious} className="control-btn">⏮</button>
            <button onClick={handlePlayPause} className="control-btn main">
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button onClick={handleNext} className="control-btn">⏭</button>
          </div>
          {playbackState.device && (
            <div className="now-playing-device">
              Playing on {playbackState.device.name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NowPlaying;
